import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database, Json } from '@/types/database'

type ObligacionRecurrente = Database['public']['Tables']['obligaciones_recurrentes']['Row']

/** Obligación recurrente + IDs de clientes puntuales (modo 'clientes', tabla puente). */
export type ObligacionRecurrenteConClientes = ObligacionRecurrente & { clientes_filtro: string[] }

/** Payload de upsert (form -> RPC transaccional). `clientes_filtro` solo aplica en modo 'clientes'. */
export interface ObligacionRecurrenteUpsertInput {
  nombre: string
  descripcion?: string | null
  categoria: string
  periodicidad: string
  dia_vencimiento: number
  mes_offset: number
  aplica_dia_habil?: boolean
  modo_asignacion: string
  condicion_iva_filtro?: string[] | null
  impuesto_filtro?: string[] | null
  clientes_filtro?: string[] | null
  color?: string | null
  activo?: boolean
}

export type { ObligacionRecurrente }

export const obligacionesRecurrentesService = {
  async listar(
    client: SupabaseClient<Database>,
    organizacionId: string
  ): Promise<ObligacionRecurrenteConClientes[]> {
    const { data: obligaciones, error } = await client
      .from('obligaciones_recurrentes')
      .select('*')
      .eq('organizacion_id', organizacionId)
      .order('nombre')

    if (error) throw error

    const ids = (obligaciones ?? []).map((o) => o.id)
    let puente: { obligacion_recurrente_id: string; cliente_id: string }[] = []
    if (ids.length > 0) {
      const { data: rows, error: errPuente } = await client
        .from('obligacion_recurrente_clientes')
        .select('obligacion_recurrente_id, cliente_id')
        .in('obligacion_recurrente_id', ids)
      if (errPuente) throw errPuente
      puente = rows ?? []
    }

    return (obligaciones ?? []).map((o) => ({
      ...o,
      clientes_filtro: puente
        .filter((p) => p.obligacion_recurrente_id === o.id)
        .map((p) => p.cliente_id),
    }))
  },

  /**
   * ORR-270: le genera a UN cliente los vencimientos recurrentes que le falten, para que un
   * cliente recién dado de alta —o uno al que se le acaban de asignar impuestos— no espere hasta
   * el cron mensual `regenerar-vencimientos-recurrentes-mensual` (día 1 de cada mes).
   *
   * Usa `generar_vencimientos_obligacion_cliente`, NO `generar_vencimientos_obligacion`. La
   * diferencia importa: la segunda hace dos DELETE para limpiar las filas de clientes que dejaron
   * de matchear el filtro, y ejecutarla desde el alta convertiría cada cliente nuevo en un evento
   * destructivo para los demás — un cliente inactivo perdería sus vencimientos futuros, y con
   * ellos las alertas que apuntan a esas filas por `ON DELETE CASCADE`. La función acotada solo
   * agrega. Lo encontró el review de Codex sobre el primer intento (PR #921).
   *
   * Las obligaciones de modo 'clientes' quedan afuera a propósito: resuelven contra una lista
   * explícita, así que al cliente nuevo hay que agregarlo a mano igual.
   *
   * Sin `p_meses_adelante`: la ventana la define el default de la función (12 meses), la misma
   * que usa el cron.
   *
   * Devuelve cuántas obligaciones se procesaron. Lanza si alguna falló, pero recorre todas
   * primero — una obligación rota no debe dejar sin generar a las demás.
   */
  async generarParaCliente(
    client: SupabaseClient<Database>,
    organizacionId: string,
    clienteId: string
  ): Promise<number> {
    const { data: obligaciones, error } = await client
      .from('obligaciones_recurrentes')
      .select('id, modo_asignacion, aplica_a_todos')
      .eq('organizacion_id', organizacionId)
      .eq('activo', true)

    if (error) throw error

    // Mismo criterio que `resolver_clientes_obligacion`:
    // COALESCE(modo_asignacion, CASE WHEN aplica_a_todos THEN 'todos' ELSE 'filtros' END)
    const dinamicas = (obligaciones ?? []).filter(
      (o) => (o.modo_asignacion ?? (o.aplica_a_todos ? 'todos' : 'filtros')) !== 'clientes'
    )

    // En paralelo: cada llamada solo inserta filas de (su obligación, este cliente).
    const resultados = await Promise.all(
      dinamicas.map((obligacion) =>
        client.rpc('generar_vencimientos_obligacion_cliente', {
          p_obligacion_id: obligacion.id,
          p_cliente_id: clienteId,
        })
      )
    )

    const primerError = resultados.find((r) => r.error)?.error
    if (primerError) throw primerError

    return dinamicas.length
  },

  /**
   * Upsert transaccional de obligación + clientes puntuales (RPC SECURITY DEFINER).
   * Valida tenant, hace insert/update y reemplaza la tabla puente en una sola transacción.
   * Devuelve el id de la obligación.
   */
  async guardar(
    client: SupabaseClient<Database>,
    params: { id: string | null; organizacionId: string; input: ObligacionRecurrenteUpsertInput }
  ): Promise<string> {
    const { id, organizacionId, input } = params
    const { clientes_filtro, ...rest } = input

    const { data, error } = await client.rpc('upsert_obligacion_recurrente_con_clientes', {
      // `p_id` NULL es el modo insert de la RPC (`IF p_id IS NULL THEN` en su definición). El
      // generador de tipos dejó de marcar los argumentos como nullable, así que el tipo quedó más
      // estricto que la función: el cast preserva el contrato real, que no cambió.
      p_id: id as string,
      p_data: { ...rest, organizacion_id: organizacionId } as unknown as Json,
      p_cliente_ids: clientes_filtro ?? [],
    })

    if (error) throw error
    return data as string
  },

  async eliminar(
    client: SupabaseClient<Database>,
    id: string,
    borrarVencimientosPendientes: boolean = true
  ): Promise<void> {
    const { error } = await client.rpc('eliminar_obligacion_recurrente_segura', {
      p_id: id,
      p_borrar_vencimientos_pendientes: borrarVencimientosPendientes,
    })
    if (error) throw error
  },

  async toggleEstado(
    client: SupabaseClient<Database>,
    id: string,
    activo: boolean
  ): Promise<void> {
    // ORR-198 A5.3: pasar por la RPC SECURITY DEFINER (valida rol) en vez de UPDATE directo por
    // PostgREST. Habilita dropear la policy RLS UPDATE de obligaciones_recurrentes tras el deploy.
    const { error } = await client.rpc('toggle_obligacion_recurrente_activa' as never, {
      p_id: id,
      p_activo: activo,
    } as never)

    if (error) throw error
  },
}
